import {
    AssigneeFilter,
    AuthContext,
    AvatarStack,
    buildSearchExtraFilters,
    CLAIM_PAYER_FILTER_ID,
    clearClaimScopedListing,
    ChatPanel,
    ComposePanel,
    CoreFetch,
    DocumentApi,
    ExportAllFetchOptions,
    FilterMethod,
    getFirstCommentBody,
    mapMessagingTableRows,
    buildCategoryNameById,
    buildUserNameById,
    IColumnFilterCustomProps,
    IColumnFiltersConfig,
    IComment,
    ICustomFilter,
    IMessage,
    IRowAction,
    ITableColumn,
    ITableFetchOptions,
    ITaskListFetchContext,
    ListProviderMessagesOrderEnum,
    loadTaskTypeCounts,
    mergeTaskListFilters,
    MessageApi,
    MessageLinkEntityType,
    MessagingProvider,
    MessagingTableRowsCache,
    ProviderClaim,
    ProviderMessage,
    ProviderMessageSortType,
    runFireAndForget,
    runTaskListFetchCycle,
    SupportMessage,
    TaskTypeId,
    ToastContext,
    UserApi,
} from '@enter/core';
import { MessageListRedesigned } from '@enter/core';
import { mdiCheck, mdiClose, mdiContentCopy } from '@mdi/js';
import { CategoryTags, useDocumentView } from 'components';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Row } from 'react-table';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DialogContext } from 'services';
import { IInjectedRootStore, IMessageDetail, IMessageListingRequest } from 'stores';
import * as S from './messaging.styles';

type IMessagingRedesignedProps = IInjectedRootStore;

export const MessagingRedesigned: React.FunctionComponent<IMessagingRedesignedProps> = inject('rootStore')(
    observer((props: IMessagingRedesignedProps) => {
        const { rootStore } = props;
        if (!rootStore) {
            return <></>;
        }
        document.title = 'ENTER Platform - Messaging';
        const navigate = useNavigate();
        const params = useParams();
        const location = useLocation();

        const authContext = useContext(AuthContext);
        const toastContext = useContext(ToastContext);
        const dialogContext = useContext(DialogContext);
        const [claims, setClaims] = useState<ProviderClaim[] | undefined>();
        const [isDetailLoading, setDetailLoading] = useState<boolean>();
        const [isListLoading, setListLoading] = useState<boolean>();
        const [fetchOptions, setFetchOptions] = useState<ITableFetchOptions>();
        const [messageDetail, setMessageDetail] = useState<IMessageDetail | undefined>();
        const [listRefreshKey, setListRefreshKey] = useState<number>(0);
        const [typeCounts, setTypeCounts] = useState<Partial<Record<TaskTypeId, number>>>({});
        const [mineCount, setMineCount] = useState<number>(0);
        const [activeTypeId, setActiveTypeId] = useState<TaskTypeId>('all');
        const [updatedMessageId, setUpdatedMessageId] = useState<string | undefined>();
        const messageChangedRef = React.useRef<boolean>(false);
        const [openFile] = useDocumentView();
        const messageDetailRef = React.useRef<IMessageDetail | undefined>(undefined);
        const isLoadingRef = React.useRef<string | undefined>(undefined);
        const populatedFromMessageRef = React.useRef<string | undefined>(undefined);
        const isInitialLoadRef = React.useRef<boolean>(true);
        const listFetchGenerationRef = React.useRef({ current: 0 });
        const listContextRef = React.useRef<ITaskListFetchContext>({
            typeId: 'all',
            mineOnly: false,
            searchTerm: '',
            searchIn: 'subject',
        });
        const failedMessageIdsRef = React.useRef<Set<string>>(new Set());
        const paramsMessageIdRef = React.useRef<string | undefined>(params.messageId);

        // Keep refs in sync with state / URL
        React.useEffect(() => {
            messageDetailRef.current = messageDetail;
        }, [messageDetail]);

        React.useEffect(() => {
            paramsMessageIdRef.current = params.messageId;
        }, [params.messageId]);

        const { roleId } = rootStore;
        const isReadOnly: boolean = roleId === 'PROVIDER_READ_ONLY';
        const isReadOnlyClaimChoice: boolean = false;

        useEffect(() => {
            return () => {
                authContext.cancelGroup && authContext.cancelGroup('messaging-list');
                authContext.cancelGroup && authContext.cancelGroup('messaging-detail');
                authContext.cancelGroup && authContext.cancelGroup('messaging-metadata');
            };
        }, [authContext]);

        useEffect(() => {
            // Load metadata in a separate cancellation group to prevent interference
            const loadMetadata = async () => {
                try {
                    authContext.setGroup && authContext.setGroup('messaging-metadata');
                    await Promise.all([
                        rootStore.messageStore.loadMessageCategories(),
                        rootStore.messageStore.loadProviderUsers(),
                    ]);
                } catch (error) {
                    console.error('Failed to load messaging metadata:', error);
                }
            };
            loadMetadata();
        }, [rootStore.messageStore, authContext]);

        // Only fetch message detail from API on page load/reload when URL has messageId but we don't have the message yet
        // All other cases (row clicks, navigation) use the message object directly
        useEffect(() => {
            const loadMessageFromUrl = async () => {
                const paramsMessageId = params.messageId;

                if (paramsMessageId && paramsMessageId !== 'compose') {
                    // Transition in progress toward a different message (e.g. close & go to next).
                    // Do not reload the soon-to-be-stale URL id — that races and restores the closed message.
                    const pendingTarget = populatedFromMessageRef.current;
                    if (pendingTarget && pendingTarget !== paramsMessageId) {
                        return;
                    }

                    // FIRST: Check if we just populated this message from a message object (row click)
                    const justPopulated = pendingTarget === paramsMessageId;

                    // SECOND: Check if we already have this message loaded
                    const currentMessageId = messageDetailRef.current?.message?.id;
                    const hasMessage = currentMessageId === paramsMessageId;

                    if (justPopulated || hasMessage) {
                        // Just populated from message object OR already have the message, skip API call
                        if (justPopulated) {
                            populatedFromMessageRef.current = undefined;
                        }
                        // Message is available locally; clear stale transient failure marker.
                        failedMessageIdsRef.current.delete(paramsMessageId);
                        isLoadingRef.current = undefined;
                        return;
                    }

                    // Only guard when we actually need to fetch from API.
                    if (failedMessageIdsRef.current.has(paramsMessageId)) {
                        isLoadingRef.current = undefined;
                        setMessageDetail(undefined);
                        setDetailLoading(false);
                        navigate('..', { replace: true, relative: 'path' });
                        return;
                    }

                    // Check if we're already loading this message (prevent duplicate fetches)
                    if (isLoadingRef.current === paramsMessageId) {
                        return;
                    }

                    // Page reload scenario: fetch message detail from API
                    const requestedId = paramsMessageId;
                    isLoadingRef.current = requestedId;
                    rootStore.messageStore.clearMessageDetailCache();
                    messageDetailRef.current = undefined;
                    setMessageDetail(undefined); // Clear old message first
                    setDetailLoading(true);
                    try {
                        // Use a separate group for message detail to avoid conflicts with list fetching
                        authContext.setGroup && authContext.setGroup('messaging-detail');

                        const detail = await rootStore.messageStore.loadMessageDetail(requestedId, true);
                        // Ignore stale responses after close/switch/navigation.
                        if (
                            detail &&
                            isLoadingRef.current === requestedId &&
                            paramsMessageIdRef.current === requestedId
                        ) {
                            rootStore.messageStore.markAsRead(requestedId);
                            failedMessageIdsRef.current.delete(requestedId);
                            // Show as read in panel; list stays unchanged until close/switch
                            const nextDetail = {
                                ...detail,
                                message: { ...detail.message, read: true },
                            };
                            messageDetailRef.current = nextDetail;
                            setMessageDetail(nextDetail);
                        }
                    } catch (err: any) {
                        const isAbortError = err?.name === 'AbortError' || err?.message?.includes('abort');
                        if (!isAbortError && paramsMessageIdRef.current === requestedId) {
                            failedMessageIdsRef.current.add(requestedId);
                            messageDetailRef.current = undefined;
                            setMessageDetail(undefined);
                            setDetailLoading(false);
                            dialogContext.showError(err);
                            navigate('..', { replace: true, relative: 'path' });
                        }
                    } finally {
                        if (isLoadingRef.current === requestedId) {
                            isLoadingRef.current = undefined;
                        }
                        if (paramsMessageIdRef.current === requestedId) {
                            setDetailLoading(false);
                        }
                    }
                } else if (!paramsMessageId || paramsMessageId === 'compose') {
                    // No message in URL, clear message detail
                    populatedFromMessageRef.current = undefined;
                    isLoadingRef.current = undefined;
                    rootStore.messageStore.clearMessageDetailCache();
                    messageDetailRef.current = undefined;
                    setMessageDetail(undefined);
                    setDetailLoading(false);
                }
            };
            loadMessageFromUrl();
        }, [params.messageId, rootStore.messageStore, authContext, dialogContext, navigate]);

        const getCurrentUserId = (): string | undefined => {
            const allUsers = rootStore.messageStore.providerUsers;
            const currentUserFromStore = allUsers.find(
                u => u.userId === rootStore.userId || u.personalAccountId === rootStore.userId,
            );
            return (
                (currentUserFromStore &&
                    (currentUserFromStore.personalAccountId || currentUserFromStore.userId)) ||
                rootStore.userId
            );
        };

        const buildSort = (
            sortBy: ITableFetchOptions['sortBy'],
        ): { sort: ProviderMessageSortType[]; order: ListProviderMessagesOrderEnum[] } => {
            const sort: ProviderMessageSortType[] = [];
            const order: ListProviderMessagesOrderEnum[] = [];
            _.forEach(sortBy, rule => {
                let s;
                switch (rule.id) {
                    case 'Subject':
                        s = ProviderMessageSortType.SUBJECT;
                        break;
                    case 'Created':
                        s = ProviderMessageSortType.CREATED;
                        break;
                    case 'Updated':
                        s = ProviderMessageSortType.UPDATED;
                        break;
                    case 'Purpose':
                        s = ProviderMessageSortType.PURPOSE;
                        break;
                    case 'Priority':
                        s = ProviderMessageSortType.PRIORITY;
                        break;
                    default:
                        s = undefined;
                }
                if (s) {
                    sort.push(s);
                    order.push(
                        rule.desc
                            ? ListProviderMessagesOrderEnum.DESCENDING
                            : ListProviderMessagesOrderEnum.ASCENDING,
                    );
                }
            });
            if (!sort.length) {
                sort.push(ProviderMessageSortType.UPDATED);
                order.push(ListProviderMessagesOrderEnum.DESCENDING);
            }
            return { sort, order };
        };

        const buildRequest = (
            newFetchData: ITableFetchOptions,
            context: ITaskListFetchContext,
            searchExtra: ICustomFilter[],
        ): IMessageListingRequest => {
            const { pageSize, pageIndex } = newFetchData;
            const { sort, order } = buildSort(newFetchData.sortBy);
            const categories = rootStore.messageStore.messageCategories || [];
            const filters = mergeTaskListFilters(
                newFetchData,
                context,
                categories,
                getCurrentUserId(),
                searchExtra,
            );
            return {
                page: pageIndex,
                pageSize,
                sort,
                order,
                filters,
            };
        };

        const resolveSearchExtra = async (
            context: ITaskListFetchContext,
            listFilters?: ICustomFilter[],
        ): Promise<ICustomFilter[] | 'empty'> => {
            return buildSearchExtraFilters({
                searchTerm: context.searchTerm,
                searchIn: context.searchIn,
                searchComments: args => rootStore.messageStore.searchCommentsByBody(args),
                searchCodings: args => rootStore.messageStore.searchCodingsByResult(args),
                listFilters,
            });
        };

        const refreshCounts = async (
            context: ITaskListFetchContext,
            listFilters: ICustomFilter[] | undefined,
            listingTotal: number,
        ): Promise<{ typeCounts: Partial<Record<TaskTypeId, number>>; mineCount: number }> => {
            const categories = rootStore.messageStore.messageCategories || [];
            const { sort, order } = buildSort([{ id: 'Updated', desc: true }]);
            return loadTaskTypeCounts({
                categories,
                context,
                currentUserId: getCurrentUserId(),
                count: async filters =>
                    rootStore.messageStore.countMessages({
                        page: 0,
                        pageSize: 1,
                        sort,
                        order,
                        filters,
                    }),
                searchExtra: () => resolveSearchExtra(context, listFilters),
                listFilters,
                listingTotal,
            });
        };

        const refresh = async (): Promise<void> => {
            if (fetchOptions) {
                await handleFetchData(fetchOptions, listContextRef.current);
            }
        };

        const handleFetchData = async (
            newFetchOptions?: ITableFetchOptions,
            context?: ITaskListFetchContext,
        ): Promise<void> => {
            const options = newFetchOptions || fetchOptions;
            const ctx = context || listContextRef.current;
            if (options) {
                listContextRef.current = ctx;
                const genAtStart = listFetchGenerationRef.current.current + 1;
                let pendingCounts:
                    | { typeCounts: Partial<Record<TaskTypeId, number>>; mineCount: number }
                    | undefined;
                try {
                    setListLoading(true);
                    authContext.cancelGroup && authContext.cancelGroup('messaging-list');
                    authContext.setGroup && authContext.setGroup('messaging-list');

                    const shouldRefreshCounts =
                        (options.pageIndex || 0) === 0 || isInitialLoadRef.current;

                    let listingRequest: IMessageListingRequest | undefined;
                    let listingTotalForRead = 0;
                    const outcome = await runTaskListFetchCycle({
                        generation: listFetchGenerationRef.current,
                        resolveSearchExtra: () => resolveSearchExtra(ctx, options.validFilters),
                        fetchListing: async (searchExtra, onPartial) => {
                            listingRequest = buildRequest(options, ctx, searchExtra);
                            return rootStore.messageStore.fetchMessageListing(listingRequest, false, onPartial);
                        },
                        applyListing: listing => {
                            const req = listingRequest || buildRequest(options, ctx, []);
                            rootStore.messageStore.applyMessageListing(req, listing);
                            listingTotalForRead = listing.total ?? 0;
                        },
                        emptyListing: { messages: [], total: 0 },
                        onEmptySearch: clearClaimScopedListing,
                        refreshCounts: async ({ listingTotal }) => {
                            listingTotalForRead = listingTotal;
                            pendingCounts = await refreshCounts(ctx, options.validFilters, listingTotal);
                        },
                        shouldRefreshCounts,
                    });

                    if (outcome === 'stale') {
                        return;
                    }

                    // Switch inbox/done bucket only after a successful apply (avoids empty flash).
                    setActiveTypeId(ctx.typeId);

                    if (listingRequest) {
                        runFireAndForget(
                            () =>
                                rootStore.messageStore.refreshReadFilterCounts(
                                    listingRequest,
                                    ctx.typeId === 'done',
                                ),
                            'refreshReadFilterCounts',
                        );
                    }

                    if (pendingCounts) {
                        // Partial update — only the active chip was refreshed.
                        setTypeCounts(prev => ({ ...prev, ...pendingCounts.typeCounts }));
                        setMineCount(pendingCounts.mineCount);
                    }
                    if (shouldRefreshCounts) {
                        isInitialLoadRef.current = false;
                    }

                    setFetchOptions(options);
                } catch (err: any) {
                    if (genAtStart === listFetchGenerationRef.current.current) {
                        if (err?.name !== 'AbortError' && !err?.message?.includes('abort')) {
                            dialogContext.showError(err);
                        }
                    }
                } finally {
                    if (genAtStart === listFetchGenerationRef.current.current) {
                        setListLoading(false);
                    }
                }
            }
        };

        const handleExportAll = async ({
            scope,
            onProgress,
            signal,
            onPage,
        }: ExportAllFetchOptions): Promise<void> => {
            const baseReq = {
                sort: [ProviderMessageSortType.UPDATED],
                order: [ListProviderMessagesOrderEnum.DESCENDING],
                filters: [] as ICustomFilter[],
            };

            authContext.setGroup && authContext.setGroup('messaging-export');
            const onAbort = (): void => {
                authContext.cancelGroup && authContext.cancelGroup('messaging-export');
            };
            signal?.addEventListener('abort', onAbort);

            try {
                await rootStore.messageStore.fetchAllMessagesForExport(baseReq, {
                    scope,
                    onProgress,
                    signal,
                    beforeFetchPage: () => {
                        authContext.setGroup && authContext.setGroup('messaging-export');
                    },
                    onPage: async (messages, info) => {
                        const mapped = messages.map(m => {
                            const categoryNames = _.compact(
                                _.map(m.links?.messageCategories, cat =>
                                    rootStore.messageStore.getCategoryName(cat.id || ''),
                                ),
                            );
                            const assigneeNames = _.compact(
                                _.map(m.links?.users, user =>
                                    rootStore.messageStore.getUserName(user.id || ''),
                                ),
                            );
                            return {
                                ...m,
                                documents: m.documentCount,
                                categories: categoryNames,
                                assignees: assigneeNames,
                                firstCommentBody: getFirstCommentBody(m as any),
                            };
                        });
                        await onPage(mapped, info);
                    },
                });
            } finally {
                signal?.removeEventListener('abort', onAbort);
            }
        };

        const refreshMessageDetail = async (mid: string, forceFetch?: boolean): Promise<void> => {
            if (mid === 'compose') {
                return;
            }
            // Use separate group for message detail refreshes
            authContext.cancelGroup && authContext.cancelGroup('messaging-detail');
            authContext.setGroup && authContext.setGroup('messaging-detail');

            const detail = await rootStore.messageStore.loadMessageDetail(mid, forceFetch);
            if (detail) {
                setMessageDetail(detail);
                setDetailLoading(false);
            }
        };

        const doSearch = async (term: string): Promise<ProviderClaim[]> => {
            setClaims([]);
            const results = await rootStore.claimStore.loadBasicClaimSearchResults(term);
            setClaims(results);
            return results;
        };

        const claimSearch = async (term: string): Promise<ProviderClaim[]> => {
            authContext.setGroup && authContext.setGroup('messaging-claim-search');
            authContext.cancelGroup && authContext.cancelGroup('messaging-claim-search');
            return await doSearch(term);
        };
        const composeComment = async ({ messageId: mid, body, files }: IComment): Promise<boolean> => {
            try {
                await rootStore.messageStore.addComment(rootStore.account, mid, body, files);
                // The comment is saved. A failed refresh must not turn that into a failed post:
                // callers (e.g. RESUME CLAIM) would leave the task open and a retry would save the
                // instruction twice. Report the refresh error, keep the result.
                await refreshMessageDetail(mid, true).catch(err => dialogContext.showError(err));
                messageChangedRef.current = true;
                return true;
            } catch (err) {
                dialogContext.showError(err);
                return false;
            }
        };
        const composeMessage = async ({
            subject,
            body,
            isPrivateNote,
            claimId,
            files,
            links,
            priority,
            dueDate,
        }: IMessage): Promise<void | string> => {
            try {
                // Extract claim IDs from links if available, otherwise use single claimId
                const claimIds = links?.claims?.map(c => c.id).filter(Boolean) || (claimId ? [claimId] : []);
                // Extract category IDs from links if available
                const categoryIds = links?.messageCategories?.map(c => c.id).filter(Boolean);
                // Extract user IDs from links if available
                const assignedUserIds = links?.users?.map(u => u.id).filter(Boolean);

                const messageId = await rootStore.messageStore.composeMessage(
                    rootStore.account,
                    subject,
                    body,
                    claimIds,
                    files,
                    priority,
                    dueDate,
                    categoryIds,
                    assignedUserIds,
                    isPrivateNote,
                );
                toastContext.notify?.('Message sent', 'left');
                return messageId;
            } catch (err) {
                dialogContext.showError(err);
            }
        };
        const downloadDocument = async (id: string): Promise<void> => {
            const file = await rootStore.messageStore.embed(id);

            file.url && openFile(file.url);
        };
        const actualDownloadDocument = async (id: string): Promise<string> => {
            const file = await rootStore.messageStore.download(id);
            return file.url || '';
        };
        const markAsOpen = useCallback(async (ids: string | string[]): Promise<void> => {
            const idList = _.compact(_.concat([], ids));
            try {
                const { opened, failed } = await rootStore.messageStore.markManyAsOpen(idList);
                messageChangedRef.current = true;

                if (failed.length && !opened.length) {
                    throw new Error(
                        `Failed to reopen ${failed.length} message${failed.length > 1 ? 's' : ''}`,
                    );
                }

                if (failed.length) {
                    toastContext.notify?.(
                        `Reopened ${opened.length} of ${idList.length} messages — ${failed.length} failed`,
                        'left',
                    );
                    return;
                }

                toastContext.notify?.(
                    `Successfully reopened ${opened.length} message${opened.length > 1 ? 's' : ''}`,
                    'left',
                );
            } catch (err) {
                dialogContext.showError(err);
                throw err;
            }
        }, [rootStore.messageStore, toastContext, dialogContext]);
        const markAsClosed = useCallback(async (ids: string | string[]): Promise<void> => {
            const idList = _.compact(_.concat([], ids));
            try {
                const { closed, failed } = await rootStore.messageStore.markManyAsClosed(idList);
                messageChangedRef.current = true;

                if (failed.length && !closed.length) {
                    throw new Error(
                        `Failed to mark ${failed.length} message${failed.length > 1 ? 's' : ''} as done`,
                    );
                }

                if (failed.length) {
                    // Partial success: don't throw. The caller clears the selection and refetches,
                    // so the ones that failed come back in the list instead of silently vanishing.
                    toastContext.notify?.(
                        `Marked ${closed.length} of ${idList.length} messages as done — ${failed.length} failed`,
                        'left',
                    );
                    return;
                }

                toastContext.notify?.(
                    `Successfully marked ${closed.length} message${closed.length > 1 ? 's' : ''} as done`,
                    'left',
                );
            } catch (err) {
                dialogContext.showError(err);
                throw err;
            }
        }, [rootStore.messageStore, toastContext, dialogContext]);
        const markAsRead = async (ids: string | string[]): Promise<void> => {
            const idList = _.concat([], ids);

            const promises = _.map(idList, async (id): Promise<void> => rootStore.messageStore.markAsRead(id));
            await Promise.all(promises);
            messageChangedRef.current = true;

            // Update detail panel optimistically (store does not mutate list message until close/switch)
            const currentDetail = messageDetailRef.current;
            const currentId = currentDetail?.message?.id;
            if (currentDetail && currentId && idList.includes(currentId)) {
                const updatedDetail: IMessageDetail = {
                    ...currentDetail,
                    message: { ...currentDetail.message, read: true },
                };
                messageDetailRef.current = updatedDetail;
                setMessageDetail(updatedDetail);
            }
        };
        const markAsUnread = async (ids: string | string[]): Promise<void> => {
            const idList = _.concat([], ids);
            const promises = _.map(idList, async (id): Promise<void> => rootStore.messageStore.markAsUnread(id));
            await Promise.all(promises);
            messageChangedRef.current = true;

            // Update detail panel optimistically (store does not mutate list message until close/switch)
            const currentDetail = messageDetailRef.current;
            const currentId = currentDetail?.message?.id;
            if (currentDetail && currentId && idList.includes(currentId)) {
                const updatedDetail: IMessageDetail = {
                    ...currentDetail,
                    message: { ...currentDetail.message, read: false },
                };
                messageDetailRef.current = updatedDetail;
                setMessageDetail(updatedDetail);
            }
        };

        const populateMessageDetail = (message: ProviderMessage): void => {
            if (message?.id) {
                // Local populate should unblock IDs that previously failed remotely.
                failedMessageIdsRef.current.delete(message.id);
            }
            // Create IMessageDetail from the message object
            const comments = Array.isArray(message.comments)
                ? _.orderBy(message.comments, 'created', 'asc')
                : undefined;
            const detail: IMessageDetail = {
                message: message,
                comments: comments,
                documents: [],
            };
            messageDetailRef.current = detail;
            setMessageDetail(detail);

            // Fetch documents asynchronously in the background
            if (comments && comments.length > 0) {
                const documentIds = _.flatMap(comments, 'documents').filter(Boolean) as string[];
                if (documentIds.length > 0) {
                    fetchDocumentsAsync(message.id!, documentIds);
                }
            }
        };

        const fetchDocumentsAsync = async (messageId: string, documentIds: string[]): Promise<void> => {
            try {
                const filterId = documentIds.map(id => `EQ:${id}`);
                const documents = await CoreFetch(DocumentApi).listProviderDocuments({ filterId });

                if (messageDetailRef.current?.message?.id === messageId) {
                    const updatedDetail: IMessageDetail = {
                        ...messageDetailRef.current,
                        documents: documents || [],
                    };
                    messageDetailRef.current = updatedDetail;
                    setMessageDetail(updatedDetail);
                }
            } catch (err) {
                console.error('[MessagingRedesigned.fetchDocumentsAsync] error', err);
            }
        };

        const messageOpen = async (message: ProviderMessage | SupportMessage): Promise<void> => {
            // In provider app, we always receive ProviderMessage
            const providerMessage = message as ProviderMessage;
            const messageId = providerMessage.id!;

            const isCompose = location.pathname.endsWith('/compose');
            const paramsMessageId = params.messageId;

            // If we're switching away from a different message that has unsynced changes, refresh once (in background)
            if (paramsMessageId && paramsMessageId !== messageId && messageChangedRef.current) {
                const message = messageDetail?.message;
                if (message) {
                    rootStore.messageStore.updateMessageInList(message);
                }
                messageChangedRef.current = false;
            }

            // Drop prior message's comments/documents so the panel never shows stale detail.
            const previousId = messageDetailRef.current?.message?.id;
            if (previousId && previousId !== messageId) {
                rootStore.messageStore.clearMessageDetailCache();
            }

            // Get the latest message object from the store
            let latestMessage = (rootStore.messageStore.findMessage(messageId) || providerMessage) as ProviderMessage;

            // If message is unread, optimistically mark it read in the detail + fire async update for list
            if (!latestMessage.read) {
                latestMessage = { ...latestMessage, read: true };
                messageChangedRef.current = true;
                void rootStore.messageStore.markAsRead(messageId);
            }

            // Set flag before navigation so useEffect knows to skip a duplicate URL-driven fetch
            populatedFromMessageRef.current = messageId;

            const embeddedComments = Array.isArray(latestMessage.comments) ? latestMessage.comments : [];

            if (embeddedComments.length > 0) {
                populateMessageDetail(latestMessage);
            } else {
                // List rows often lack comments — load full detail so ChatBody can render
                setDetailLoading(true);
                try {
                    authContext.setGroup && authContext.setGroup('messaging-detail');
                    const detail = await rootStore.messageStore.loadMessageDetail(messageId, true);
                    if (detail?.message) {
                        failedMessageIdsRef.current.delete(messageId);
                        const nextDetail = {
                            ...detail,
                            message: { ...detail.message, read: true },
                        };
                        messageDetailRef.current = nextDetail;
                        setMessageDetail(nextDetail);
                    } else {
                        populateMessageDetail(latestMessage);
                    }
                } catch (error) {
                    console.error('Failed to load message detail on open:', error);
                    populateMessageDetail(latestMessage);
                } finally {
                    setDetailLoading(false);
                }
            }

            // Navigate to the message
            if (isCompose) {
                navigate(`../${messageId}`, { relative: 'path' });
            } else if (paramsMessageId) {
                navigate(`../${messageId}`, { relative: 'path' });
            } else {
                navigate(messageId);
            }
        };
        const messageClose = async (): Promise<void> => {
            // If there were any changes while the panel was open, refresh the list once on close (in background)
            if (messageChangedRef.current) {
                const message = messageDetail?.message;
                if (message) {
                    rootStore.messageStore.updateMessageInList(message);
                }
                messageChangedRef.current = false;
            }
            rootStore.messageStore.clearMessageDetailCache();
            messageDetailRef.current = undefined;
            setMessageDetail(undefined);
            navigate('..', { replace: true, relative: 'path' });
        };

        const closeAndGoToNext = async (id: string): Promise<void> => {
            const inbox = rootStore.messageStore.inboxMessages;
            const currentIndex = inbox.findIndex(m => m.id === id);
            const nextId =
                (currentIndex >= 0
                    ? inbox.slice(currentIndex + 1).find(m => !!m.id)?.id
                    : undefined) || inbox.find(m => m.id && m.id !== id)?.id;

            // Detach closed message from panel/context BEFORE markAsClosed (toast/store updates
            // can re-run the URL effect while params still point at `id`).
            messageChangedRef.current = false;
            isLoadingRef.current = undefined;
            populatedFromMessageRef.current = nextId;
            rootStore.messageStore.clearMessageDetailCache();
            messageDetailRef.current = undefined;
            setClaims(undefined);
            setMessageDetail(undefined);
            setDetailLoading(!!nextId);

            await markAsClosed(id);
            setListRefreshKey(prev => prev + 1);

            if (!nextId) {
                populatedFromMessageRef.current = undefined;
                await messageClose();
                return;
            }

            try {
                authContext.setGroup && authContext.setGroup('messaging-detail');
                const detail = await rootStore.messageStore.loadMessageDetail(nextId, true);
                // Abandon if user navigated away during the load.
                if (populatedFromMessageRef.current !== nextId && paramsMessageIdRef.current !== nextId) {
                    return;
                }
                if (!detail?.message) {
                    populatedFromMessageRef.current = undefined;
                    setMessageDetail(undefined);
                    await messageClose();
                    return;
                }

                failedMessageIdsRef.current.delete(nextId);
                const wasUnread = !detail.message.read;
                const nextDetail = {
                    ...detail,
                    message: { ...detail.message, read: true },
                };
                messageDetailRef.current = nextDetail;
                setMessageDetail(nextDetail);

                if (wasUnread) {
                    void rootStore.messageStore.markAsRead(nextId);
                }

                const isCompose = location.pathname.endsWith('/compose');
                const paramsMessageId = params.messageId;
                if (isCompose || paramsMessageId) {
                    navigate(`../${nextId}`, { relative: 'path' });
                } else {
                    navigate(nextId);
                }
            } catch (error) {
                console.error('Failed to open next message after close:', error);
                populatedFromMessageRef.current = undefined;
                messageDetailRef.current = undefined;
                setMessageDetail(undefined);
                await messageClose();
            } finally {
                setDetailLoading(false);
            }
        };

        const showCompose = (): void => {
            const paramsMessageId = params.messageId;
            if (paramsMessageId) {
                navigate('../compose', { relative: 'path' });
            } else {
                navigate('compose');
            }
        };

        const {
            doneTotal,
            doneMessages,
            inboxTotal,
            inboxMessages,
            unreadInboxFilteredTotal,
            unreadDoneFilteredTotal,
            allInboxFilteredTotal,
            allDoneFilteredTotal,
        } = rootStore.messageStore;

        // Stable refs so MessageListRedesigned.columns cache is not busted every observer tick.
        const customColumns: ITableColumn[] = useMemo(
            () => [
                {
                    id: 'Categories',
                    Header: 'Categories',
                    accessor: 'categories',
                    Cell: ({ cell }: any): React.ReactElement => <CategoryTags categories={cell.value} />,
                    minWidth: 150,
                    maxWidth: 500,
                    disableSortBy: true,
                    show: true,
                },
                {
                    id: 'Assignee',
                    Header: 'Assignees',
                    accessor: 'assignees',
                    Cell: ({ cell }: any): React.ReactElement => <AvatarStack names={cell.value} />,
                    minWidth: 150,
                    maxWidth: 500,
                    disableSortBy: true,
                    show: true,
                },
            ],
            [],
        );

        const rowActions: IRowAction[] = useMemo(
            () => [
                {
                    icon: mdiCheck,
                    tooltip: 'Mark as done',
                    action: async (row: Row<any>) => {
                        const messageId = row.original.id;
                        if (messageId) {
                            await markAsClosed(messageId);
                            // Row removal is reactive (MobX), but the tab chip counts and
                            // "N tasks need your action" subtitle are plain React state that
                            // only refreshes via a table refetch — force one.
                            setListRefreshKey(prev => prev + 1);
                            toastContext.notify?.('Message marked as done', 'left');
                        }
                    },
                    isVisible: (row: Row<any>) => row.original.open === true,
                },
                {
                    icon: mdiClose,
                    tooltip: 'Mark as undone',
                    action: async (row: Row<any>) => {
                        const messageId = row.original.id;
                        if (messageId) {
                            await markAsOpen(messageId);
                            setListRefreshKey(prev => prev + 1);
                            toastContext.notify?.('Message marked as undone', 'left');
                        }
                    },
                    isVisible: (row: Row<any>) => row.original.open === false,
                },
                {
                    icon: mdiContentCopy,
                    tooltip: 'Copy message ID',
                    action: (row: Row<any>) => {
                        const messageId = row.original.id;
                        if (messageId) {
                            navigator.clipboard.writeText(messageId);
                            toastContext.notify?.('Message ID copied to clipboard', 'left');
                        }
                    },
                },
            ],
            [markAsClosed, markAsOpen, toastContext, setListRefreshKey],
        );

        const inboxRowsCacheRef = React.useRef<MessagingTableRowsCache<ProviderMessage> | null>(null);
        const doneRowsCacheRef = React.useRef<MessagingTableRowsCache<ProviderMessage> | null>(null);
        const messageStore = rootStore.messageStore;

        const categoryNameById = useMemo(
            () => buildCategoryNameById(messageStore.messageCategories),
            [messageStore.messageCategories],
        );
        const userNameById = useMemo(
            () => buildUserNameById(messageStore.providerUsers),
            [messageStore.providerUsers],
        );
        const lastKnownReadByMessageId = messageStore.lastKnownReadByMessageId;
        const currentlyOpenMessageId = messageDetail?.message?.id;

        const mappedInboxMessages = useMemo(() => {
            const { rows, cache } = mapMessagingTableRows(inboxMessages, {
                previous: inboxRowsCacheRef.current,
                getCategoryNames: m =>
                    _.compact(
                        _.map(m.links?.messageCategories, cat =>
                            cat.id ? categoryNameById.get(cat.id) : undefined,
                        ),
                    ),
                getAssigneeNames: m =>
                    _.compact(
                        _.map(m.links?.users, user => (user.id ? userNameById.get(user.id) : undefined)),
                    ),
                getAssigneeIds: m => _.compact(_.map(m.links?.users, user => user.id)),
                getRead: m => {
                    const shouldApplyOverride = m.id !== currentlyOpenMessageId;
                    return shouldApplyOverride && m.id
                        ? (lastKnownReadByMessageId[m.id] ?? m.read)
                        : m.read;
                },
            });
            inboxRowsCacheRef.current = cache;
            return rows as any;
        }, [
            inboxMessages,
            inboxTotal,
            currentlyOpenMessageId,
            categoryNameById,
            userNameById,
            lastKnownReadByMessageId,
        ]);

        const mappedDoneMessages = useMemo(() => {
            const { rows, cache } = mapMessagingTableRows(doneMessages, {
                previous: doneRowsCacheRef.current,
                getCategoryNames: m =>
                    _.compact(
                        _.map(m.links?.messageCategories, cat =>
                            cat.id ? categoryNameById.get(cat.id) : undefined,
                        ),
                    ),
                getAssigneeNames: m =>
                    _.compact(
                        _.map(m.links?.users, user => (user.id ? userNameById.get(user.id) : undefined)),
                    ),
                getAssigneeIds: m => _.compact(_.map(m.links?.users, user => user.id)),
                getRead: m => {
                    const shouldApplyOverride = m.id !== currentlyOpenMessageId;
                    return shouldApplyOverride && m.id
                        ? (lastKnownReadByMessageId[m.id] ?? m.read)
                        : m.read;
                },
            });
            doneRowsCacheRef.current = cache;
            return rows as any;
        }, [
            doneMessages,
            doneTotal,
            currentlyOpenMessageId,
            categoryNameById,
            userNameById,
            lastKnownReadByMessageId,
        ]);

        const renderMessageList = (): React.ReactElement | undefined => {
            const loadUserOptions = async (searchTerm: string) => {
                const users = rootStore.messageStore.providerUsers;
                return users
                    .filter(user => {
                        const fullName = `${user.first || ''} ${user.last || ''}`.trim();
                        return (
                            !searchTerm ||
                            fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            user.email?.toLowerCase().includes(searchTerm.toLowerCase())
                        );
                    })
                    .map(user => ({
                        label: `${user.first || ''} ${user.last || ''}`.trim() || user.email || 'Unknown User',
                        value: user.personalAccountId || '',
                    }));
            };

            const accountId = params.accountId;
            const loadAuthorOptions = async (searchTerm: string) => {
                const userOptions = await loadUserOptions(searchTerm);
                const term = (searchTerm || '').toLowerCase();
                const fixedOptions: { label: string; value: string }[] = [
                    { label: 'Enter, Inc.', value: '678242de-f591-40cd-ab70-839c2e7981d2' },
                    ...(accountId ? [{ label: 'Enter Support', value: accountId }] : []),
                ].filter(opt => !term || opt.label.toLowerCase().includes(term));
                return [...fixedOptions, ...userOptions];
            };

            const columnFiltersConfig: IColumnFiltersConfig = {
                Subject: {
                    filterType: 'String',
                    filterId: 'filterSubject',
                    label: 'Filter by Subject',
                    placeholder: 'Enter subject...',
                    defaultMethod: FilterMethod.LK,
                },
                Purpose: {
                    filterType: 'String',
                    filterId: 'filterPurpose',
                    label: 'Filter by Purpose',
                    placeholder: 'Enter purpose...',
                    defaultMethod: FilterMethod.LK,
                },
                Priority: {
                    filterType: 'Select',
                    filterId: 'filterPriority',
                    label: 'Filter by Priority',
                    defaultMethod: FilterMethod.EQ,
                    treatNullAsEmpty: true,
                    options: [
                        { label: 'Low', value: 'LOW' },
                        { label: 'Normal', value: 'NORMAL' },
                        { label: 'High', value: 'HIGH' },
                        { label: 'Urgent', value: 'URGENT' },
                        { label: 'None', value: null },
                    ],
                },
                Categories: {
                    filterType: 'AsyncSelect',
                    filterId: 'filterMessageCategoryId',
                    label: 'Filter by Categories',
                    defaultMethod: FilterMethod.EQ,
                    isMulti: true,
                    loadOptions: async (searchTerm: string) => {
                        const categories = rootStore.messageStore.messageCategories;
                        return categories
                            .filter(cat => !searchTerm || cat.name?.toLowerCase().includes(searchTerm.toLowerCase()))
                            .map(cat => ({
                                label: cat.name || '',
                                value: cat.id || '',
                            }));
                    },
                },
                Assignees: {
                    filterType: 'Custom',
                    filterId: 'filterUserId',
                    label: 'Filter by Assignees',
                    defaultMethod: FilterMethod.EQ,
                    component: React.memo((props: IColumnFilterCustomProps) => {
                        const allUsers = rootStore.messageStore.providerUsers;
                        const currentUserFromStore = allUsers.find(
                            u => u.userId === rootStore.userId || u.personalAccountId === rootStore.userId,
                        );
                        const currentUserId =
                            (currentUserFromStore &&
                                (currentUserFromStore.personalAccountId || currentUserFromStore.userId)) ||
                            rootStore.userId;

                        const currentUserLabel = 'Self';

                        return (
                            <AssigneeFilter
                                {...props}
                                loadOptions={loadUserOptions}
                                currentUserId={currentUserId}
                                currentUserLabel={currentUserLabel}
                            />
                        );
                    }),
                },
                Patient: {
                    filterType: 'AsyncSelect',
                    filterId: 'filterPatientName',
                    label: 'Filter by Patient',
                    defaultMethod: FilterMethod.EQ,
                    isMulti: true,
                    loadOptions: async (searchTerm: string) => {
                        const patients = await rootStore.claimStore.queryPatients(searchTerm);
                        return _.map(patients, p => ({
                            label: `${p.first || ''} ${p.last || ''}`.trim() || p.id || '',
                            value: p.id || '',
                        }));
                    },
                },
                // Tasks on the payer's claims (CA-3441). Hidden in demo: generated tasks are not
                // linked to claims the scope could select, so the list would not change.
                ...(rootStore.isDemo()
                    ? {}
                    : {
                          Payer: {
                              filterType: 'AsyncSelect' as const,
                              filterId: CLAIM_PAYER_FILTER_ID,
                              label: 'Filter by Payer',
                              defaultMethod: FilterMethod.EQ,
                              isMulti: true,
                              loadOptions: async (searchTerm: string) => {
                                  const payers = await rootStore.claimStore.queryPayers(searchTerm);
                                  return _.map(payers, p => ({
                                      label: p.name || p.id || '',
                                      value: p.id || '',
                                  }));
                              },
                          },
                      }),
            };

            // Note: Removed dataLoadedKey - was causing duplicate fetches when categories/users loaded
            // The component will re-render normally when data changes via MobX observer

            const activeMessages = activeTypeId === 'done' ? mappedDoneMessages : mappedInboxMessages;
            const activeTotal = activeTypeId === 'done' ? doneTotal : inboxTotal;

            return (
                <MessageListRedesigned
                    showCodingSearch={!rootStore.isDemo()}
                    showCoderRuleSearch={!rootStore.isDemo()}
                    showDosFilter={!rootStore.isDemo()}
                    messages={activeMessages}
                    total={activeTotal}
                    typeCounts={typeCounts}
                    mineCount={mineCount}
                    onFetchData={handleFetchData}
                    onShowCompose={showCompose}
                    onExportAll={handleExportAll}
                    onExportAllError={message => toastContext.notify?.(message, 'left')}
                    selectedMessageId={messageDetail?.message?.id}
                    customColumns={customColumns}
                    columnFiltersConfig={columnFiltersConfig}
                    loadAuthorOptions={loadAuthorOptions}
                    rowActions={rowActions}
                    refreshKey={listRefreshKey}
                    currentUserId={getCurrentUserId()}
                    currentUserLabel="Self"
                    unreadCount={
                        activeTypeId === 'done' ? unreadDoneFilteredTotal : unreadInboxFilteredTotal
                    }
                    allFilteredCount={
                        activeTypeId === 'done' ? allDoneFilteredTotal : allInboxFilteredTotal
                    }
                />
            );
        };

        const renderComposePanel = (): React.ReactElement | undefined => {
            const state = window.history.state?.usr || {};
            const claimId = state.claimId;
            const subject = state.subject;
            const body = state.body;
            if (!isReadOnly) {
                return (
                    <S.ChatPanelContainer>
                        <ComposePanel
                            claimId={claimId}
                            subject={subject}
                            body={body}
                            hideNote={true}
                            enableProviderNote={true}
                            className="fixed"
                        />
                    </S.ChatPanelContainer>
                );
            }
        };

        const paramsMessageId = params.messageId;
        const isComposePath = location.pathname.endsWith('/compose');

        const linkEntityToMessage = async (
            id: string,
            entityType: MessageLinkEntityType,
            entityIds: string[],
        ): Promise<void> => {
            try {
                // Skip reload in store method; mark list as dirty instead
                await rootStore.messageStore.linkEntityToMessage(id, entityType, entityIds, true);
                setClaims([]);
                messageChangedRef.current = true;
                await refreshMessageDetail(id, true);
            } catch (error) {
                console.error('Failed to link entity:', error);
                throw error;
            }
        };

        const unlinkEntityFromMessage = async (
            id: string,
            entityType: MessageLinkEntityType,
            entityIds: string[],
        ): Promise<void> => {
            try {
                // Skip reload in store method; mark list as dirty instead
                await rootStore.messageStore.unlinkEntityFromMessage(id, entityType, entityIds, true);
                setClaims([]);
                messageChangedRef.current = true;
                await refreshMessageDetail(id, true);
            } catch (error) {
                console.error('Failed to unlink entity:', error);
                throw error;
            }
        };

        return (
            <MessagingProvider
                isReadOnlyClaimChoice={isReadOnlyClaimChoice}
                isReadOnly={isReadOnly}
                isListLoading={isListLoading}
                isDetailLoading={isDetailLoading}
                account={rootStore.account}
                enableMessageTypeValidation={true}
                message={messageDetail ? messageDetail.message : undefined}
                comments={messageDetail ? messageDetail.comments : undefined}
                documents={messageDetail ? messageDetail.documents : undefined}
                claims={claims}
                getCategoryName={rootStore.messageStore.getCategoryName}
                getUserName={rootStore.messageStore.getUserName}
                onClaimSearch={claimSearch}
                onClaimsResolve={async (claimIds: string[]) => {
                    // `claimStore.loadClaim` branches on `features.isDemo`, so this returns
                    // generated claims in a demo and real ones otherwise — the card never has to
                    // know which.
                    const resolved = await Promise.all(claimIds.map(id => rootStore.claimStore.loadClaim(id)));
                    return _.compact(resolved);
                }}
                // Demo mode only (FE-81). The coding card fetches through the API client, which demo mode
                // refuses, so a halted demo task showed no rules. A real account keeps the card's own fetch.
                onCodingsResolve={rootStore.isDemo() ? rootStore.messageStore.resolveDemoCodings : undefined}
                onComposeComment={composeComment}
                onComposeMessage={composeMessage}
                onDocumentDownload={downloadDocument}
                onDocumentActualDownload={actualDownloadDocument}
                onMarkAsClosed={markAsClosed}
                onMarkAsOpen={markAsOpen}
                onMarkAsRead={markAsRead}
                onMarkAsUnread={markAsUnread}
                onMessageClose={messageClose}
                onMessageOpen={messageOpen}
                onCloseAndGoToNext={closeAndGoToNext}
                onLinkEntityToMessage={linkEntityToMessage}
                onUnlinkEntityFromMessage={unlinkEntityFromMessage}
                onUpdateMessage={async (id, payload) => {
                    await rootStore.messageStore.updateMessage(id, payload, true);
                    await refreshMessageDetail(id, true);
                    messageChangedRef.current = true;
                }}
                onMessageFieldsUpdate={async () => {
                    messageChangedRef.current = true;
                }}>
                {renderMessageList()}
                {isComposePath && renderComposePanel()}
                {paramsMessageId && !isComposePath && (
                    <S.ChatPanelContainer>
                        <ChatPanelImpl onBack={() => navigate('..', { replace: true, relative: 'path' })} />
                    </S.ChatPanelContainer>
                )}
            </MessagingProvider>
        );
    }),
);

const ChatPanelImpl: React.FC<{
    onBack: () => void;
}> = ({ onBack }) => {
    return <ChatPanel hideNote={true} className="fixed" onBack={onBack} />;
};
